import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/ToastManager';

// スタイル定義
const styles = {
  button: {
    padding: '12px 20px',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  pageButton: {
    padding: '8px 12px',
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    fontSize: '14px',
    cursor: 'pointer'
  }
};

// タブボタンコンポーネント
const TabButton = ({ active, label, count, onClick }) => (
  <button
    style={{
      padding: '12px 24px',
      border: 'none',
      borderBottom: active ? '3px solid #09529f' : '3px solid transparent',
      backgroundColor: active ? '#f8f9fa' : 'transparent',
      color: active ? '#09529f' : '#666',
      fontSize: '16px',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    }}
    onClick={onClick}
    onMouseEnter={(e) => !active && (e.target.style.backgroundColor = '#f8f9fa')}
    onMouseLeave={(e) => !active && (e.target.style.backgroundColor = 'transparent')}
  >
    {`${label} (${count})`}
  </button>
);

// ページネーションコンポーネント
const Pagination = ({ pagination, onPageChange }) => {
  if (pagination.totalPages <= 1) return null;

  const { page: current, totalPages } = pagination;
  const pages = [];
  const start = Math.max(0, current - 2);
  const end = Math.min(totalPages - 1, current + 2);

  // ページ番号ボタン生成
  const PageButton = ({ pageNum, isCurrent }) => (
    <button
      onClick={() => onPageChange(pageNum)}
      style={{
        ...styles.pageButton,
        backgroundColor: isCurrent ? '#09529f' : 'white',
        color: isCurrent ? 'white' : '#09529f',
        fontWeight: isCurrent ? 'bold' : 'normal',
        cursor: isCurrent ? 'default' : 'pointer'
      }}
    >
      {pageNum + 1}
    </button>
  );

  // 最初のページ
  if (start > 0) {
    pages.push(<PageButton key={0} pageNum={0} isCurrent={false} />);
    if (start > 1) pages.push(<span key="dots-start" style={{ padding: '8px' }}>...</span>);
  }

  // 中間ページ
  for (let i = start; i <= end; i++) {
    pages.push(<PageButton key={i} pageNum={i} isCurrent={i === current} />);
  }

  // 最後のページ
  if (end < totalPages - 1) {
    if (end < totalPages - 2) pages.push(<span key="dots-end" style={{ padding: '8px' }}>...</span>);
    pages.push(<PageButton key={totalPages - 1} pageNum={totalPages - 1} isCurrent={false} />);
  }

  /**
   * ナビゲーションボタン（前へ/次へ）
   */
  const NavButton = ({ direction, disabled, onClick }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...styles.button,
        backgroundColor: disabled ? '#e9ecef' : 'white',
        color: disabled ? '#6c757d' : '#09529f',
        cursor: disabled ? 'not-allowed' : 'pointer'
      }}
    >
      {direction === 'prev' ? '← 前へ' : '次へ →'}
    </button>
  );

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '15px', padding: '10px' }}>
      <NavButton direction="prev" disabled={current === 0} onClick={() => onPageChange(current - 1)} />
      {pages}
      <NavButton direction="next" disabled={current === totalPages - 1} onClick={() => onPageChange(current + 1)} />
      <div style={{ marginLeft: '20px', fontSize: '14px', color: '#666' }}>
        {`${current + 1} / ${totalPages} ページ (全${pagination.totalElements ?? ''}件)`}
      </div>
    </div>
  );
};

const PAGE_SIZE = 10;

const StoreHistoryPage = ({ memberData, onBack }) => {
  const { getCsrfTokenFromCookie } = useAuth();
  const { showError } = useToast();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('PRIMARY');
  const [primaryCount, setPrimaryCount] = useState(0);
  const [additionalCount, setAdditionalCount] = useState(0);
  const [pagination, setPagination] = useState({
    page: 0,
    totalElements: 0,
    totalPages: 0
  });

  // 店舗変更履歴をAPIから取得
  const fetchStoreChangeHistory = useCallback(async (storeType, page) => {
    try {
      setLoading(true);
      setError(null);

      const csrfToken = await getCsrfTokenFromCookie(true);
      if (!csrfToken) throw new Error('セキュリティトークンの取得に失敗しました');

      const params = new URLSearchParams({
        storeType,
        page: String(page),
        size: String(PAGE_SIZE)
      });

      const response = await fetch(`/api/me/store-change-history?${params}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 401) throw new Error('認証エラー: ログインし直してください');
        if (response.status === 404) throw new Error('会員情報が見つかりません');
        throw new Error(errorData.message || `店舗変更履歴の取得に失敗しました (${response.status})`);
      }

      const data = await response.json();

      setEntries(data.entries || []);
      setPrimaryCount(data.primaryCount ?? 0);
      setAdditionalCount(data.additionalCount ?? 0);
      setPagination({
        page: data.page ?? 0,
        totalElements: data.totalElements ?? 0,
        totalPages: data.totalPages ?? 0
      });
    } catch (err) {
      console.error('[STORE HISTORY] Error:', err);
      setError(err.message);
      showError(err.message);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [getCsrfTokenFromCookie, showError]);

  // 初回読み込み
  useEffect(() => {
    fetchStoreChangeHistory(activeTab, 0);
    // タブ切り替え時は handleTabChange 側で取得するため、ここは初回のみ実行する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // タブ切り替え
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    fetchStoreChangeHistory(newTab, 0);
  };

  // ページ切り替え → API再取得
  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < pagination.totalPages) {
      fetchStoreChangeHistory(activeTab, newPage);
    }
  };

  // APIは適用日を "YYYY-MM-DD" で返す。
  // Date に変換するとタイムゾーンで前日にずれることがあるため文字列のまま整形する。
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const [year, month, day] = dateString.slice(0, 10).split('-');
    if (!year || !month || !day) return dateString;
    return `${year}/${month}/${day}`;
  };

  if (loading) {
    return (
      <div style={{ padding: '40px 20px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <h1 style={{ fontSize: '32px', color: '#09529f', fontWeight: 'bold' }}>所属店舗履歴</h1>
          <button style={{ ...styles.button, backgroundColor: '#09529f', color: 'white' }} onClick={onBack}>
            ← ホームに戻る
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '40px', fontSize: '16px', color: '#666' }}>
          店舗変更履歴を読み込み中...
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px 20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* ヘッダー */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '15px' }}>
        <h1 style={{ fontSize: '32px', color: '#09529f', fontWeight: 'bold' }}>所属店舗履歴</h1>
        <button
          style={{ ...styles.button, backgroundColor: '#09529f', color: 'white' }}
          onClick={onBack}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#002348'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#09529f'}
        >
          ← ホームに戻る
        </button>
      </div>

      {/* エラー表示 */}
      {error && (
        <div style={{
          textAlign: 'center',
          padding: '20px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '5px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {/* 日付の意味の補足 */}
      <p style={{ fontSize: '14px', color: '#666', lineHeight: 1.7, marginBottom: '20px' }}>
        利用開始日・利用終了日は、実際に所属店舗が切り替わった日を表示しています。
      </p>

      {/* タブ */}
      <div style={{ marginBottom: '20px', borderBottom: '2px solid #dee2e6' }}>
        <div style={{ display: 'flex', gap: '0' }}>
          <TabButton
            active={activeTab === 'PRIMARY'}
            label="主店舗"
            count={primaryCount}
            onClick={() => handleTabChange('PRIMARY')}
          />
          <TabButton
            active={activeTab === 'ADDITIONAL'}
            label="追加店舗"
            count={additionalCount}
            onClick={() => handleTabChange('ADDITIONAL')}
          />
        </div>
      </div>

      {/* テーブル */}
      {entries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666', fontSize: '16px' }}>
          {`${activeTab === 'PRIMARY' ? '主店舗' : '追加店舗'}の履歴データがありません`}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            tableLayout: 'fixed'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#09529f', color: 'white' }}>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: 'bold', fontSize: '14px' }}>店舗</th>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: 'bold', fontSize: '14px' }}>利用開始日</th>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: 'bold', fontSize: '14px' }}>利用終了日</th>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: 'bold', fontSize: '14px' }}>所属状況</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((history, index) => (
                <tr
                  key={`${history.storeName}-${history.startDate}-${index}`}
                  style={{
                    borderBottom: '1px solid #dee2e6',
                    backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#f8f9fa'}
                >
                  <td style={{ padding: '16px', fontSize: '14px', color: '#333', textAlign: 'center' }}>
                    {history.storeName}
                  </td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#666', textAlign: 'center' }}>
                    {formatDate(history.startDate)}
                  </td>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#666', textAlign: 'center' }}>
                    {formatDate(history.endDate)}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    {history.isActive ? (
                      <span style={{
                        display: 'inline-block',
                        backgroundColor: '#e8f0fb',
                        color: '#09529f',
                        border: '1px solid #09529f',
                        borderRadius: '12px',
                        padding: '3px 12px',
                        fontSize: '13px',
                        fontWeight: 'bold'
                      }}>
                        利用中
                      </span>
                    ) : (
                      <span style={{
                        display: 'inline-block',
                        backgroundColor: '#f8f9fa',
                        color: '#666',
                        border: '1px solid #dee2e6',
                        borderRadius: '12px',
                        padding: '3px 12px',
                        fontSize: '13px'
                      }}>
                        利用終了
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ページネーション */}
      <Pagination pagination={pagination} onPageChange={handlePageChange} />
    </div>
  );
};

export default StoreHistoryPage;